import { Search, FileText, Users, TrendingUp, Crown, Star, ArrowRight, Building, Smartphone, Wifi, ChevronRight, Target, Upload, DollarSign, Calendar, Plus, Clock, CheckCircle, AlertCircle, User, MapPin, ChevronDown, Zap, PenTool, CalendarDays, Trophy } from "lucide-react";
import { getIconByName } from "@/components/team/performanceMetricsConfig";
import type { TeamPerformanceMetric, TeamMemberPerformance } from "@/types/team";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

import { AppSidebar } from "@/components/AppSidebar";

import TaskWidget from "@/components/TaskWidget";
import { HomeTodos } from "@/components/HomeTodos";
import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { NotificationBell } from '@/components/NotificationBell';
import { Task } from "@/types/database";
import { toast } from "@/hooks/use-toast";
import { FollowUpReorganizer } from "@/components/FollowUpReorganizer";
import { D2DFollowUpCalendar } from "@/components/d2d/D2DFollowUpCalendar";
import { AppAssistant } from "@/components/AppAssistant";

const Index = () => {
  
  const { user } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Array<{ id: string; name: string; }>>([]);
  const [todos, setTodos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [globalLeadCounts, setGlobalLeadCounts] = useState({ hot: 0, followup: 0, na: 0 });
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isTimelineOpen, setIsTimelineOpen] = useState(true);
  const [showCalendarView, setShowCalendarView] = useState(false);
  const [userName, setUserName] = useState<string>('');
  const [teamId, setTeamId] = useState<string | null>(null);
  const [performanceMetrics, setPerformanceMetrics] = useState<TeamPerformanceMetric[]>([]);
  const [performances, setPerformances] = useState<TeamMemberPerformance[]>([]);

  // Get time-based greeting
  const getGreeting = () => {
    const hour = currentTime.getHours();
    const minutes = currentTime.getMinutes();
    if (hour >= 5 && hour < 12) return 'Guten Morgen';
    if (hour < 17 || (hour === 17 && minutes < 30)) return 'Guten Tag';
    return 'Guten Abend';
  };

  // Fetch user profile for name and team
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user?.id) return;
      
      const { data } = await supabase
        .from('profiles')
        .select('first_name, team_id')
        .eq('user_id', user.id)
        .single();
      
      if (data?.first_name) {
        setUserName(data.first_name);
      }
      if (data?.team_id) {
        setTeamId(data.team_id);
      }
    };
    
    fetchUserProfile();
  }, [user?.id]);

  // Fetch performance metrics and performances from database
  useEffect(() => {
    const fetchPerformanceData = async () => {
      if (!user?.id || !teamId) return;

      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59).toISOString();

      const [metricsResult, performancesResult] = await Promise.all([
        supabase
          .from('team_performance_metrics')
          .select('*')
          .eq('team_id', teamId)
          .eq('is_active', true)
          .order('sort_order', { ascending: true }),
        supabase
          .from('team_member_performance')
          .select('*')
          .eq('user_id', user.id)
          .gte('recorded_at', startOfMonth)
          .lte('recorded_at', endOfMonth)
      ]);

      if (metricsResult.data) {
        setPerformanceMetrics(metricsResult.data);
      }
      if (performancesResult.data) {
        setPerformances(performancesResult.data);
      }
    };

    fetchPerformanceData();

    // Realtime subscription for performance updates
    if (!user?.id || !teamId) return;

    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

    const channel = supabase
      .channel('performance-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'team_member_performance',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          const newPerformance = payload.new as TeamMemberPerformance;
          // Only add if it's from current month
          if (new Date(newPerformance.recorded_at) >= new Date(startOfMonth)) {
            setPerformances(prev => [...prev, newPerformance]);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'team_member_performance'
        },
        (payload) => {
          const deletedId = (payload.old as any).id;
          setPerformances(prev => prev.filter(p => p.id !== deletedId));
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, teamId]);
  const [todayFollowUps, setTodayFollowUps] = useState<Array<{
    id: string;
    time: string;
    address: string;
    name?: string;
    source: 'running_list' | 'd2d_units';
    manual_reminder?: boolean;
    isHot?: boolean;
    isKT?: boolean;
  }>>([]);
  const timelineScrollRef = useRef<HTMLDivElement>(null);

  // Initialize sidebar state from localStorage, default to true (expanded)
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('sidebarState');
    return saved ? JSON.parse(saved) : true;
  });

  // Save sidebar state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('sidebarState', JSON.stringify(isSidebarOpen));
  }, [isSidebarOpen]);
  // Removed excessive logging to improve performance

  // If no user, show loading or redirect
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Loading...</h1>
          <p>Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Load customers from localStorage (legacy support)
  useEffect(() => {
    const savedCustomers = localStorage.getItem("vodafone-customers");
    if (savedCustomers) {
      const customerData = JSON.parse(savedCustomers);
      const customerOptions = customerData.map((customer: any) => ({
        id: customer.id,
        name: customer.name
      }));
      setCustomers(customerOptions);
    }
  }, []);

  // Load todos from database with realtime updates
  useEffect(() => {
    if (!user) return;

    const loadTodos = async () => {
      try {
        const { data: todosData, error } = await supabase
          .from('todos')
          .select('*')
          .eq('user_id', user.id)
          .order('due_date', { ascending: true });
        
        if (error) throw error;
        setTodos(todosData || []);
      } catch (error) {
        console.error('Error loading todos:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTodos();

    // Set up realtime subscription for todos
    const channel = supabase
      .channel('todos-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'todos',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('Todo change detected:', payload);
          // Reload todos when any change occurs
          loadTodos();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  // Load global lead counts for D2D - optimized single query with joins
  const loadGlobalLeadCounts = async () => {
    if (!user?.id) return;

    try {
      // Single optimized query with nested joins instead of 4 sequential queries
      const { data, error } = await supabase
        .from('d2d_units')
        .select(`
          id, tags, last_contact_at, next_reminder,
          d2d_houses!inner(
            id,
            d2d_streets!inner(
              id, is_active,
              d2d_areas!inner(id, is_active, created_by)
            )
          )
        `)
        .eq('user_id', user.id)
        .eq('d2d_houses.d2d_streets.is_active', true)
        .eq('d2d_houses.d2d_streets.d2d_areas.is_active', true)
        .eq('d2d_houses.d2d_streets.d2d_areas.created_by', user.id);

      if (error) throw error;

      const counts = {
        hot: (data || []).filter(unit => 
          unit.tags?.includes('HOT') || 
          (unit.tags?.includes('KI') && unit.last_contact_at && 
           new Date(unit.last_contact_at) > new Date(Date.now() - 24 * 60 * 60 * 1000))
        ).length,
        followup: (data || []).filter(unit => {
          if (unit.tags?.includes('WIN') || unit.tags?.includes('NA') || unit.tags?.includes('KI')) return false;
          if (unit.tags?.includes('KT') || unit.tags?.includes('HOT')) return true;
          if (!unit.next_reminder) return false;
          
          const reminderDate = new Date(unit.next_reminder);
          const today = new Date();
          const tomorrow = new Date(today);
          tomorrow.setDate(tomorrow.getDate() + 1);
          
          return reminderDate.toDateString() === today.toDateString() || 
                 reminderDate.toDateString() === tomorrow.toDateString() || 
                 reminderDate <= today;
        }).length,
        na: (data || []).filter(unit => unit.tags?.includes('NA')).length
      };

      setGlobalLeadCounts(counts);
    } catch (error) {
      console.error('Error loading global lead counts:', error);
    }
  };

  // Load global counts when user changes
  useEffect(() => {
    loadGlobalLeadCounts();
  }, [user?.id]);

  // Update clock every minute instead of every second
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // 60 seconds instead of 1 second

    return () => clearInterval(interval);
  }, []);

  // Center timeline on current hour
  useEffect(() => {
    if (!timelineScrollRef.current) return;
    
    const currentHour = currentTime.getHours();
    const scrollContainer = timelineScrollRef.current;
    
    // Calculate scroll position to center current hour
    const itemWidth = 88; // Approximate width of each hour item
    const containerWidth = scrollContainer.clientWidth;
    const currentHourIndex = Math.max(0, currentHour - 9); // Hours start from 9
    const scrollLeft = (currentHourIndex * itemWidth) - (containerWidth / 2) + (itemWidth / 2);
    
    scrollContainer.scrollTo({
      left: Math.max(0, scrollLeft),
      behavior: 'smooth'
    });
  }, [currentTime]);

  // Load today's follow-ups
  const loadTodayFollowUps = async () => {
    if (!user) return;

    try {
      const today = new Date();
      const dayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const dayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

      // Load from running_list_entries
      const { data: runningListData } = await supabase
        .from('running_list_entries')
        .select('id, street, house_number, apartment_unit, follow_up_scheduled, notes')
        .eq('user_id', user.id)
        .gte('follow_up_scheduled', dayStart.toISOString())
        .lte('follow_up_scheduled', dayEnd.toISOString())
        .not('follow_up_scheduled', 'is', null);

      // Load from d2d_units - only from active areas
      const { data: d2dUnitsData } = await supabase
        .from('d2d_units')
        .select(`
          id, 
          unit_name, 
          tags,
          last_contact_at,
          next_reminder, 
          follow_up_notes,
          manual_reminder,
          d2d_houses!inner(
            house_number,
            d2d_streets!inner(
              name,
              plz,
              d2d_areas!inner(
                id,
                is_active
              )
            )
          )
        `)
        .eq('user_id', user.id)
        .eq('d2d_houses.d2d_streets.d2d_areas.is_active', true)
        .gte('next_reminder', dayStart.toISOString())
        .lte('next_reminder', dayEnd.toISOString())
        .not('next_reminder', 'is', null);

      const followUps: Array<{
        id: string;
        time: string;
        address: string;
        name?: string;
        source: 'running_list' | 'd2d_units';
        manual_reminder?: boolean;
        isHot?: boolean;
        isKT?: boolean;
      }> = [];

      // Process running list entries
      if (runningListData) {
        runningListData.forEach(entry => {
          const time = new Date(entry.follow_up_scheduled).toLocaleTimeString('de-DE', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: false 
          });
          const address = `${entry.street} ${entry.house_number}`;
          const name = entry.apartment_unit || address;
          followUps.push({
            id: `rl_${entry.id}`,
            time,
            address,
            name,
            source: 'running_list',
            manual_reminder: true, // Running list entries are considered manual
            isHot: false, // Running list entries are not marked as hot
            isKT: false // Running list entries are not KT
          });
        });
      }

      // Process d2d units - only from active areas
      if (d2dUnitsData) {
        d2dUnitsData.forEach(unit => {
          const time = new Date(unit.next_reminder).toLocaleTimeString('de-DE', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: false 
          });
          const address = `${unit.d2d_houses.d2d_streets.name} ${unit.d2d_houses.house_number}`;
          const name = unit.unit_name || address;
          
          // Determine if this is a hot lead (has HOT tag or recent KI tag)
          const isHot = unit.tags?.includes('HOT') || 
                       (unit.tags?.includes('KI') && unit.last_contact_at && 
                        new Date(unit.last_contact_at) > new Date(Date.now() - 24 * 60 * 60 * 1000));
          
          // Check if this is a KT (customer contact)
          const isKT = unit.tags?.includes('KT');
          
          followUps.push({
            id: `d2d_${unit.id}`,
            time,
            address,
            name,
            source: 'd2d_units',
            manual_reminder: unit.manual_reminder,
            isHot,
            isKT
          });
        });
      }

      // Sort by time
      followUps.sort((a, b) => a.time.localeCompare(b.time));
      setTodayFollowUps(followUps);
    } catch (error) {
      console.error('Error loading today\'s follow-ups:', error);
    }
  };

  // Update overdue follow-ups then load today's follow-ups
  const updateOverdueAndLoadFollowUps = async () => {
    if (!user) return;
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        loadTodayFollowUps();
        return;
      }

      // Call edge function to reschedule any overdue follow-ups
      await supabase.functions.invoke('update-overdue-followups', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        }
      });
    } catch (error) {
      console.error('Error updating overdue follow-ups:', error);
    } finally {
      // Load follow-ups after updating
      loadTodayFollowUps();
    }
  };

  // Load today's follow-ups when component mounts
  useEffect(() => {
    updateOverdueAndLoadFollowUps();
  }, [user]);

  // Navigate directly to a specific unit from leads
  const handleNavigateToUnit = async (areaId: string, streetId: string, houseId: string, unitId: string) => {
    // For the homepage, just redirect to running list
    navigate('/running-list');
  };

  // Calculate todo statistics
  const getDaysUntilDue = (dueDate: string | null) => {
    if (!dueDate) return null;
    const now = new Date();
    const due = new Date(dueDate);
    const diffTime = due.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const completedTodos = todos.filter(t => t.completed);
  const pendingTodos = todos.filter(t => !t.completed);
  const overdueTodos = pendingTodos.filter(t => t.due_date && getDaysUntilDue(t.due_date) < 0);
  const stats = [
    {
      icon: FileText,
      title: "15.000+",
      description: "Dokumente verfügbar",
      color: "text-blue-600"
    },
    {
      icon: Users,
      title: "50.000+",
      description: "Aktive Nutzer",
      color: "text-green-600"
    },
    {
      icon: TrendingUp,
      title: "99.9%",
      description: "Verfügbarkeit",
      color: "text-purple-600"
    }
  ];

  const features = [
    {
      icon: Building,
      title: "Unternehmensstrategie",
      description: "Zugang zu allen strategischen Dokumenten, Geschäftsplänen und internen Richtlinien für eine erfolgreiche Unternehmensführung.",
      premium: false
    },
    {
      icon: Smartphone,
      title: "Technische Dokumentation",
      description: "Umfassende technische Handbücher, API-Dokumentationen und Implementierungsleitfäden für alle Services.",
      premium: true
    },
    {
      icon: Wifi,
      title: "Netzwerk & Infrastruktur",
      description: "Detaillierte Informationen über Netzwerkarchitektur, 5G-Implementation und Infrastrukturdokumentation.",
      premium: true
    }
  ];

  const currentMonth = new Date().toLocaleDateString('de-DE', { year: 'numeric', month: 'long' });
  
  // Calculate stats from real performance data
  const metricStats = performanceMetrics.map(metric => {
    const metricPerformances = performances.filter(p => p.metric_id === metric.id);
    const count = metricPerformances.length;
    const points = metricPerformances.reduce((sum, p) => sum + p.points, 0);
    return { metric, count, points };
  });
  
  const totalPerformancePoints = metricStats.reduce((sum, s) => sum + s.points, 0);
  const totalPerformanceCount = metricStats.reduce((sum, s) => sum + s.count, 0);

  return (
    <SidebarProvider open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
      <div className="min-h-screen flex w-full bg-gradient-subtle min-w-0">
        {/* Sidebar */}
        <AppSidebar />
        
        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0 max-w-full">
          {/* Top Bar */}
          <header className="h-12 flex items-center border-b bg-card/80 backdrop-blur-sm sticky top-0 z-40">
            <SidebarTrigger className="ml-2" />
            <div className="flex-1 flex items-center justify-center px-4">
              <span className="text-base font-semibold text-foreground">
                {getGreeting()} {userName || ''}
              </span>
            </div>
            <div className="flex items-center gap-4 pr-4">
              <NotificationBell />
            </div>
          </header>

          {/* Content Area */}
          <div className="flex-1 overflow-auto">
            <div className="container mx-auto px-4 py-8">
              
              {/* Timeline Section */}
              <section className="mb-8">
                <div className="max-w-6xl mx-auto">
                  <Collapsible open={isTimelineOpen} onOpenChange={setIsTimelineOpen}>
                    <div className="bg-card border rounded-lg">
                      <CollapsibleTrigger className="w-full p-4 hover:bg-muted/20 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <h3 className="text-sm font-medium text-muted-foreground">Heutiger Zeitplan</h3>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowCalendarView(true);
                              }}
                              className="p-1.5 hover:bg-muted rounded-md transition-colors"
                              title="Kalenderansicht"
                            >
                              <CalendarDays className="h-4 w-4 text-muted-foreground" />
                            </button>
                          </div>
                          <ChevronDown className={cn("h-4 w-4 transition-transform", isTimelineOpen && "rotate-180")} />
                        </div>
                      </CollapsibleTrigger>
                      
                      <CollapsibleContent className="px-4 pb-4">
                        {/* Timeline Container */}
                        <div className="relative">
                          {/* Scrollable timeline for all devices */}
                          <div className="mb-4">
                            <div ref={timelineScrollRef} className="overflow-x-auto pb-2">
                              <div className="flex gap-6 px-4 min-w-max">
                                {Array.from({ length: 13 }, (_, i) => i + 9).map(hour => {
                                  const hourFollowUps = todayFollowUps.filter(followUp => {
                                    const [hours] = followUp.time.split(':').map(Number);
                                    return hours === hour;
                                  });
                                  
                                  return (
                                    <div key={hour} className="flex flex-col items-center min-w-16">
                                      <div className="text-xs text-muted-foreground mb-2 font-medium">
                                        {hour.toString().padStart(2, '0')}:00
                                      </div>
                                      <div className="relative h-16 w-6 bg-muted/20 rounded-full flex flex-col-reverse items-center justify-start gap-1 py-1">
                                        {hourFollowUps.map((followUp) => {
                                          // Follow-ups should only appear as missed after midnight (new day)
                                          // Since we load today's follow-ups, they stay red until midnight
                                          const isMissed = false;
                                          
                                          return (
                                            <div
                                              key={followUp.id}
                                             className={`w-2 h-8 rounded-sm cursor-pointer transition-all duration-200 hover:w-3 ${
                                                 isMissed 
                                                   ? 'bg-gray-400 hover:bg-gray-500' 
                                                   : followUp.isHot
                                                     ? 'bg-orange-500 hover:bg-orange-600'
                                                     : followUp.isKT
                                                       ? 'bg-blue-500 hover:bg-blue-600'
                                                       : 'bg-red-500 hover:bg-red-600'
                                               }`}
                                               title={`${followUp.time} - ${followUp.name} ${isMissed ? '(Verpasst)' : ''} ${followUp.isHot ? '🔥' : followUp.isKT ? '👤' : ''}`}
                                               onClick={() => {
                                                 if (followUp.source === 'd2d_units') {
                                                   // For Index page, just redirect to running list
                                                   navigate('/running-list');
                                                 } else {
                                                   toast({
                                                     title: "Info",
                                                     description: "Diese Funktion ist nur für D2D-Einträge verfügbar.",
                                                   });
                                                 }
                                               }}
                                            />
                                          );
                                        })}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                         
                          {/* Follow-up list */}
                          {todayFollowUps.length > 0 && (
                            <div className="mt-4 space-y-2">
                              <h4 className="text-xs font-medium text-muted-foreground">Heutige Follow-Ups:</h4>
                              <div className="space-y-1 max-h-32 overflow-y-auto">
                                {todayFollowUps.map((followUp) => (
                                  <div 
                                    key={followUp.id} 
                                   className={`flex items-center justify-between text-xs rounded px-2 py-1 cursor-pointer transition-colors ${
                                     followUp.isHot 
                                       ? 'bg-destructive/20 text-destructive-foreground border border-destructive/30 hover:bg-destructive/30' 
                                       : 'bg-muted/20 hover:bg-muted/30'
                                   }`}
                                   onClick={() => {
                                     if (followUp.source === 'd2d_units') {
                                       // For Index page, just redirect to running list
                                       navigate('/running-list');
                                     } else {
                                       toast({
                                         title: "Info",
                                         description: "Diese Funktion ist nur für D2D-Einträge verfügbar.",
                                       });
                                     }
                                   }}
                                  >
                                     <div className="flex items-center gap-2">
                                       {followUp.manual_reminder ? (
                                         <PenTool className="h-3 w-3 text-blue-500" />
                                       ) : (
                                         <Zap className="h-3 w-3 text-green-500" />
                                       )}
                                       <span className="font-mono font-medium">{followUp.time}</span>
                                     </div>
                                     <div className="flex items-center gap-1 ml-2">
                                       {followUp.isHot && <span className="text-orange-500">🔥</span>}
                                       <span className="text-muted-foreground truncate">{followUp.name}</span>
                                     </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                </div>
              </section>

              {/* D2D Lead Cards */}
              <section className="mb-8">
                <div className="max-w-6xl mx-auto">
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <User className="h-5 w-5" />
                    Leads
                  </h2>
                  <div className="grid grid-cols-3 gap-4">
                    <Card 
                      className="cursor-pointer hover:bg-red-50 hover:border-red-200 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                      onClick={() => navigate('/running-list', { state: { selectedLeadType: 'hot' } })}
                    >
                      <CardContent className="p-4">
                        <div className="flex flex-col items-center text-center gap-2">
                          <User className="h-6 w-6 text-red-500" />
                          <div>
                            <p className="text-xl font-bold text-red-600">
                              {globalLeadCounts.hot}
                            </p>
                            <p className="text-xs font-medium text-muted-foreground">Hot Leads</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card 
                      className="cursor-pointer hover:bg-yellow-50 hover:border-yellow-200 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                      onClick={() => navigate('/running-list', { state: { selectedLeadType: 'followup' } })}
                    >
                      <CardContent className="p-4">
                        <div className="flex flex-col items-center text-center gap-2">
                          <Clock className="h-6 w-6 text-yellow-500" />
                          <div>
                            <p className="text-xl font-bold text-yellow-600">
                              {globalLeadCounts.followup}
                            </p>
                            <p className="text-xs font-medium text-muted-foreground">Follow-ups</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card 
                      className="cursor-pointer hover:bg-gray-50 hover:border-gray-200 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                      onClick={() => navigate('/running-list', { state: { selectedLeadType: 'na' } })}
                    >
                      <CardContent className="p-4">
                        <div className="flex flex-col items-center text-center gap-2">
                          <User className="h-6 w-6 text-gray-500" />
                          <div>
                            <p className="text-xl font-bold text-gray-600">
                              {globalLeadCounts.na}
                            </p>
                            <p className="text-xs font-medium text-muted-foreground">Nicht Angetroffen</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </section>

              {/* Performance Section */}
              <section className="mb-8">
                <div className="max-w-6xl mx-auto">
                  <Card className="border-2 border-primary/20 bg-gradient-to-r from-primary/5 to-transparent">
                    <CardHeader className="px-4 md:px-6">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                            <TrendingUp className="h-5 w-5 text-primary" />
                          </div>
                          <div className="min-w-0">
                            <CardTitle className="text-lg md:text-xl truncate">Leistungserfassung {currentMonth}</CardTitle>
                            <CardDescription className="text-sm">
                              Ihre Aufträge und Performance im Überblick
                            </CardDescription>
                          </div>
                        </div>
                        <Link to="/performance" className="shrink-0">
                          <Button variant="outline" size="sm" className="gap-2 w-full sm:w-auto">
                            <Plus className="h-4 w-4" />
                            <span className="hidden sm:inline">Auftrag hinzufügen</span>
                            <span className="sm:hidden">Neu</span>
                          </Button>
                        </Link>
                      </div>
                    </CardHeader>
                    <CardContent className="px-4 md:px-6">
                      {metricStats.length > 0 ? (
                        <>
                          <div className="flex items-center gap-3 p-3 rounded-lg bg-gradient-to-r from-primary/10 to-transparent mb-4">
                            <div className="p-2 rounded-lg bg-primary">
                              <TrendingUp className="h-4 w-4 text-white" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-muted-foreground">Gesamt {currentMonth}</p>
                              <p className="text-xl font-bold">{totalPerformanceCount} ({totalPerformancePoints} Pkt.)</p>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                            {metricStats.map(({ metric, count }) => {
                              const MetricIcon = getIconByName(metric.icon || 'trophy');
                              return (
                                <div key={metric.id} className="flex items-center gap-3 p-3 rounded-lg bg-card/50">
                                  <div className="p-2 rounded bg-primary/10">
                                    <MetricIcon className="h-4 w-4 text-primary" />
                                  </div>
                                  <div>
                                    <p className="text-xs font-medium text-muted-foreground">{metric.name}</p>
                                    <p className="text-lg md:text-xl font-bold">{count}</p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-6">
                          <Trophy className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
                          <p className="text-sm text-muted-foreground">Keine Metriken definiert</p>
                          <Link to="/performance">
                            <Button variant="outline" size="sm" className="mt-2 gap-2">
                              <Plus className="h-4 w-4" />
                              Zur Leistungserfassung
                            </Button>
                          </Link>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </section>

              {/* Task Overview Cards */}
              <section className="mb-8">
                <div className="max-w-6xl mx-auto">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card 
                      className="cursor-pointer hover:bg-muted/50 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                      onClick={() => navigate('/todos')}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-muted-foreground">Gesamt</p>
                            <p className="text-2xl font-bold">
                              {loading ? '...' : todos.length}
                            </p>
                          </div>
                          <Clock className="h-8 w-8 text-muted-foreground" />
                        </div>
                      </CardContent>
                    </Card>

                    <Card 
                      className="cursor-pointer hover:bg-red-50 hover:border-red-200 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                      onClick={() => navigate('/todos', { state: { filter: 'overdue' } })}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-muted-foreground">Überfällig</p>
                            <p className="text-2xl font-bold text-red-500">
                              {loading ? '...' : overdueTodos.length}
                            </p>
                          </div>
                          <AlertCircle className="h-8 w-8 text-red-500" />
                        </div>
                      </CardContent>
                    </Card>

                    <Card 
                      className="cursor-pointer hover:bg-green-50 hover:border-green-200 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                      onClick={() => navigate('/todos', { state: { filter: 'completed' } })}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-muted-foreground">Abgeschlossen</p>
                            <p className="text-2xl font-bold text-green-500">
                              {loading ? '...' : completedTodos.length}
                            </p>
                          </div>
                          <CheckCircle className="h-8 w-8 text-green-500" />
                        </div>
                        {!loading && todos.length > 0 && (
                          <div className="mt-2">
                            <Progress 
                              value={completedTodos.length / todos.length * 100} 
                              className="mb-1" 
                            />
                            <p className="text-xs text-muted-foreground text-center">
                              {Math.round(completedTodos.length / todos.length * 100)}% abgeschlossen
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </section>

              {/* To-Dos Widget */}
              <section className="mb-8">
                <div className="max-w-6xl mx-auto">
                  <HomeTodos />
                </div>
              </section>

              {/* Task Widget */}
              <section className="mb-12">
                <div className="max-w-6xl mx-auto">
                  <TaskWidget />
                </div>
              </section>


              {/* Hero Section */}
              <section className="text-center py-12 md:py-20">
                <div className="max-w-4xl mx-auto">
                  <h1 className="text-4xl md:text-6xl font-bold bg-gradient-text bg-clip-text text-transparent mb-6">
                    D2D+
                  </h1>
                  <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto">
                    Ihr zentraler Zugang zu allen Ressourcen, technischen Dokumentationen und Performance-Tools
                  </p>
                  
                  {/* Search Bar */}
                  <div className="relative max-w-2xl mx-auto mb-8">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground h-5 w-5" />
                    <Input 
                      placeholder="Suchen Sie nach Dokumenten, Strategien, technischen Infos..."
                      className="pl-12 pr-4 py-6 text-lg rounded-full border-2 focus:border-primary"
                    />
                    <Button 
                      size="lg" 
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 rounded-full px-8"
                      variant="vodafone"
                    >
                      Suchen
                    </Button>
                  </div>

                  <div className="flex flex-wrap justify-center gap-4 mb-12">
                    <Link to="/archive">
                      <Button size="lg" variant="vodafone" className="gap-2">
                        <ArrowRight className="h-5 w-5" />
                        Zum Archiv
                      </Button>
                    </Link>
                    <Link to="/subscription">
                      <Button size="lg" variant="outline" className="gap-2">
                        <Crown className="h-5 w-5" />
                        Premium werden
                      </Button>
                    </Link>
                    <Link to="/performance">
                      <Button size="lg" variant="outline" className="gap-2">
                        <TrendingUp className="h-5 w-5" />
                        Leistungserfassung
                      </Button>
                    </Link>
                    <Link to="/customers">
                      <Button size="lg" variant="outline" className="gap-2">
                        <Users className="h-5 w-5" />
                        Kundenverwaltung
                      </Button>
                    </Link>
                  </div>
                </div>
              </section>

              {/* Stats Section */}
              <section className="py-12">
                <div className="max-w-6xl mx-auto">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {stats.map((stat, index) => (
                      <Card key={index} className="text-center border-0 bg-card/50 backdrop-blur-sm">
                        <CardContent className="pt-6">
                          <stat.icon className={`h-12 w-12 mx-auto mb-4 ${stat.color}`} />
                          <h3 className="text-3xl font-bold mb-2">{stat.title}</h3>
                          <p className="text-muted-foreground">{stat.description}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </section>

              {/* Features Section */}
              <section className="py-20">
                <div className="max-w-6xl mx-auto text-center">
                  <h2 className="text-3xl md:text-5xl font-bold mb-4">
                    Alles was Sie brauchen
                  </h2>
                  <p className="text-xl text-muted-foreground mb-16 max-w-2xl mx-auto">
                    Von strategischen Dokumenten bis hin zu technischen Spezifikationen - 
                    Ihr komplettes Wissen an einem Ort.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {features.map((feature, index) => (
                      <Card key={index} className="relative group hover:shadow-elegant transition-all duration-300">
                        {feature.premium && (
                          <div className="absolute -top-3 -right-3">
                            <Badge className="bg-gradient-premium text-white">
                              <Crown className="h-3 w-3 mr-1" />
                              Premium
                            </Badge>
                          </div>
                        )}
                        
                        <CardHeader>
                          <div className="flex items-center gap-3 mb-3">
                            <div className="p-3 rounded-lg bg-gradient-primary/10">
                              <feature.icon className="h-6 w-6 text-primary" />
                            </div>
                            <CardTitle className="text-xl">{feature.title}</CardTitle>
                          </div>
                        </CardHeader>
                        
                        <CardContent>
                          <CardDescription className="text-base leading-relaxed">
                            {feature.description}
                          </CardDescription>
                          
                          <Button 
                            variant="ghost" 
                            className="mt-4 group-hover:bg-primary/10 transition-colors"
                            asChild
                          >
                            <Link to="/archive" className="gap-2">
                              Erkunden
                              <ChevronRight className="h-4 w-4" />
                            </Link>
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </section>

              {/* CTA Section */}
              <section className="py-20">
                <Card className="max-w-4xl mx-auto bg-gradient-primary text-white border-0">
                  <CardContent className="text-center p-12">
                    <Crown className="h-16 w-16 mx-auto mb-6 opacity-90" />
                    <h2 className="text-3xl md:text-4xl font-bold mb-4">
                      Bereit für Premium?
                    </h2>
                    <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">
                      Schalten Sie exklusive Inhalte frei und erhalten Sie Zugang zu 
                      fortgeschrittenen Features und Kundenverwaltungs-Tools.
                    </p>
                    
                    <div className="flex flex-wrap justify-center gap-4">
                      <Link to="/subscription">
                        <Button size="lg" variant="secondary" className="gap-2">
                          <Crown className="h-5 w-5" />
                          Premium werden
                        </Button>
                      </Link>
                      <Link to="/archive">
                        <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary">
                          Kostenlos starten
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </section>

              {/* Footer */}
              <footer className="border-t border-border py-12 mt-20">
                <div className="max-w-6xl mx-auto text-center">
                  <div className="flex items-center justify-center gap-2 mb-6">
                    <div className="h-6 w-6 bg-gradient-primary rounded"></div>
                    <span className="text-lg font-semibold">D2D+</span>
                  </div>
                  
                  <p className="text-muted-foreground mb-6">
                    Ihr zentraler Zugang zu allen Ressourcen und Dokumentationen.
                  </p>
                  
                  <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
                    <Link to="/archive" className="hover:text-foreground transition-colors">
                      Archiv
                    </Link>
                    <Link to="/performance" className="hover:text-foreground transition-colors">
                      Leistung
                    </Link>
                    <Link to="/subscription" className="hover:text-foreground transition-colors">
                      Preise
                    </Link>
                    <Link to="/customers" className="hover:text-foreground transition-colors">
                      Kundenverwaltung
                    </Link>
                  </div>
                </div>
              </footer>
            </div>
          </div>
        </div>
      </div>

      {/* Calendar View Modal */}
      {user && (
        <D2DFollowUpCalendar 
          open={showCalendarView}
          onOpenChange={setShowCalendarView}
          userId={user.id}
        />
      )}

      {/* AI Assistant */}
      <AppAssistant />
    </SidebarProvider>
  );
};

export default Index;